import React, { useState, useContext } from "react";
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  InputAdornment,
  IconButton,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { AdminDataContext } from "../../../useContext/AdminContext";
import API from "../../../api";

const CreateUser = () => {
  const { snackbar } = useContext(AdminDataContext);

  const [values, setValues] = useState({
    userType: "",
    email: "",
    phone: "",
    fullName: "",
    password: "",
    showPassword: false,
  });

  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({
    userType: "",
    email: "",
    phone: "",
    fullName: "",
    password: "",
  });

  const handleChange = (prop) => (event) => {
    let value = event.target.value;

    // For phone field, only allow numeric values
    if (prop === "phone") {
      value = value.replace(/[^0-9]/g, "");
    }

    setValues({ ...values, [prop]: value });
    // Clear error when user starts typing
    if (errors[prop]) {
      setErrors({ ...errors, [prop]: "" });
    }
  };

  const handleClickShowPassword = () => {
    setValues({
      ...values,
      showPassword: !values.showPassword,
    });
  };

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  const validateForm = () => {
    let isValid = true;
    const newErrors = {
      userType: "",
      email: "",
      phone: "",
      fullName: "",
      password: "",
    };

    // User Type validation
    if (!values.userType) {
      newErrors.userType = "User type is required";
      isValid = false;
    }

    // Full Name validation
    if (!values.fullName.trim()) {
      newErrors.fullName = "Full name is required";
      isValid = false;
    } else if (values.fullName.trim().length < 3) {
      newErrors.fullName = "Full name must be at least 3 characters";
      isValid = false;
    }

    // Email validation
    if (!values.email.trim()) {
      newErrors.email = "Email is required";
      isValid = false;
    } else if (!/\S+@\S+\.\S+/.test(values.email)) {
      newErrors.email = "Please enter a valid email";
      isValid = false;
    }

    // Phone validation
    if (!values.phone.trim()) {
      newErrors.phone = "Phone number is required";
      isValid = false;
    } else if (!/^[0-9]{10}$/.test(values.phone.trim())) {
      newErrors.phone = "Phone number must be 10 digits";
      isValid = false;
    }

    // Password validation
    if (!values.password) {
      newErrors.password = "Password is required";
      isValid = false;
    } else if (values.password.length < 6) {
      newErrors.password = "Password must be at least 6 characters";
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleCreateUser = async () => {
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);

    try {
      const response = await API.post("/api/v1/users/register", {
        userType: values.userType,
        email: values.email.trim(),
        phone: values.phone.trim(),
        fullName: values.fullName.trim(),
        password: values.password,
      });

      if (response.data.success) {
        snackbar("User created successfully!", "success");
        // Reset form
        setValues({
          userType: "",
          email: "",
          phone: "",
          fullName: "",
          password: "",
          showPassword: false,
        });
        setErrors({
          userType: "",
          email: "",
          phone: "",
          fullName: "",
          password: "",
        });
      }
    } catch (error) {
      const errorMessage =
        error?.response?.data?.message ||
        "Failed to create user. Please try again.";
      snackbar(errorMessage, "error");
    } finally {
      setIsLoading(false);
    }
  };

  const handleReset = () => {
    setValues({
      userType: "",
      email: "",
      phone: "",
      fullName: "",
      password: "",
      showPassword: false,
    });
    setErrors({
      userType: "",
      email: "",
      phone: "",
      fullName: "",
      password: "",
    });
  };

  const handleKeyPress = (event) => {
    if (event.key === "Enter") {
      handleCreateUser();
    }
  };

  return (
    <Box
      sx={{
        p: { xs: 2, md: 3 },
        maxWidth: 500,
        mx: "auto",
      }}
    >
      {/* Header */}
      <Typography
        variant="h4"
        sx={{
          mb: 4,
          fontSize: { xs: "1.5rem", md: "1.75rem" },
          color: "#333",
          fontWeight: 600,
          textAlign: "center",
        }}
      >
        Create New User
      </Typography>

      {/* Create User Form */}
      <Paper
        sx={{
          p: { xs: 3, md: 4 },
          backgroundColor: "#ffffff",
          boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.08)",
          borderRadius: 2,
        }}
      >
        {/* User Type Field */}
        <Box sx={{ mb: 3 }}>
          <Typography
            variant="body2"
            sx={{
              mb: 1,
              fontWeight: 500,
              color: "#333",
            }}
          >
            User Type
          </Typography>
          <FormControl fullWidth error={!!errors.userType} disabled={isLoading}>
            <Select
              size="small"
              value={values.userType}
              onChange={handleChange("userType")}
              displayEmpty
              sx={{
                backgroundColor: "#F5F5F5",
                borderRadius: "8px",
                "& fieldset": {
                  borderColor: "transparent",
                },
                "&:hover fieldset": {
                  borderColor: "#42A5F5",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "#42A5F5",
                  borderWidth: 2,
                },
                "&.Mui-error fieldset": {
                  borderColor: "#d32f2f",
                },
              }}
            >
              <MenuItem value="" disabled>
                <em>Select user type</em>
              </MenuItem>
              <MenuItem value="SUPER_ADMIN">Super Admin</MenuItem>
              <MenuItem value="ADMIN">Admin</MenuItem>
            </Select>
            {errors.userType && (
              <Typography
                variant="caption"
                sx={{ color: "#d32f2f", mt: 0.5, ml: 1.75 }}
              >
                {errors.userType}
              </Typography>
            )}
          </FormControl>
        </Box>

        {/* Full Name Field */}
        <Box sx={{ mb: 3 }}>
          <Typography
            variant="body2"
            sx={{
              mb: 1,
              fontWeight: 500,
              color: "#333",
            }}
          >
            Full Name
          </Typography>
          <TextField
            size="small"
            fullWidth
            placeholder="Enter full name"
            value={values.fullName}
            onChange={handleChange("fullName")}
            onKeyPress={handleKeyPress}
            error={!!errors.fullName}
            helperText={errors.fullName}
            disabled={isLoading}
            autoComplete="name"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <PersonOutlineIcon sx={{ color: "#999" }} />
                </InputAdornment>
              ),
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                backgroundColor: "#F5F5F5",
                borderRadius: "8px",
                "& fieldset": {
                  borderColor: "transparent",
                },
                "&:hover fieldset": {
                  borderColor: "#42A5F5",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "#42A5F5",
                  borderWidth: 2,
                },
                "&.Mui-error fieldset": {
                  borderColor: "#d32f2f",
                },
              },
            }}
          />
        </Box>

        {/* Email Field */}
        <Box sx={{ mb: 3 }}>
          <Typography
            variant="body2"
            sx={{
              mb: 1,
              fontWeight: 500,
              color: "#333",
            }}
          >
            Email
          </Typography>
          <TextField
            size="small"
            fullWidth
            placeholder="Enter email address"
            type="email"
            value={values.email}
            onChange={handleChange("email")}
            onKeyPress={handleKeyPress}
            error={!!errors.email}
            helperText={errors.email}
            disabled={isLoading}
            autoComplete="email"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <EmailIcon sx={{ color: "#999" }} />
                </InputAdornment>
              ),
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                backgroundColor: "#F5F5F5",
                borderRadius: "8px",
                "& fieldset": {
                  borderColor: "transparent",
                },
                "&:hover fieldset": {
                  borderColor: "#42A5F5",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "#42A5F5",
                  borderWidth: 2,
                },
                "&.Mui-error fieldset": {
                  borderColor: "#d32f2f",
                },
              },
            }}
          />
        </Box>

        {/* Phone Field */}
        <Box sx={{ mb: 3 }}>
          <Typography
            variant="body2"
            sx={{
              mb: 1,
              fontWeight: 500,
              color: "#333",
            }}
          >
            Phone Number
          </Typography>
          <TextField
            size="small"
            fullWidth
            placeholder="Enter 10 digit phone number"
            type="tel"
            value={values.phone}
            onChange={handleChange("phone")}
            onKeyPress={handleKeyPress}
            error={!!errors.phone}
            helperText={errors.phone}
            disabled={isLoading}
            autoComplete="tel"
            inputProps={{
              maxLength: 10,
              inputMode: "numeric",
              pattern: "[0-9]*",
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <PhoneIcon sx={{ color: "#999" }} />
                </InputAdornment>
              ),
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                backgroundColor: "#F5F5F5",
                borderRadius: "8px",
                "& fieldset": {
                  borderColor: "transparent",
                },
                "&:hover fieldset": {
                  borderColor: "#42A5F5",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "#42A5F5",
                  borderWidth: 2,
                },
                "&.Mui-error fieldset": {
                  borderColor: "#d32f2f",
                },
              },
            }}
          />
        </Box>

        {/* Password Field */}
        <Box sx={{ mb: 4 }}>
          <Typography
            variant="body2"
            sx={{
              mb: 1,
              fontWeight: 500,
              color: "#333",
            }}
          >
            Password
          </Typography>
          <TextField
            fullWidth
            size="small"
            placeholder="Enter password"
            type={values.showPassword ? "text" : "password"}
            value={values.password}
            onChange={handleChange("password")}
            onKeyPress={handleKeyPress}
            error={!!errors.password}
            helperText={errors.password}
            disabled={isLoading}
            autoComplete="new-password"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LockOutlinedIcon sx={{ color: "#999" }} />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={handleClickShowPassword}
                    onMouseDown={handleMouseDownPassword}
                    edge="end"
                    sx={{
                      color: "#999",
                      "&:hover": {
                        backgroundColor: "transparent",
                      },
                    }}
                  >
                    {values.showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                backgroundColor: "#F5F5F5",
                borderRadius: "8px",
                "& fieldset": {
                  borderColor: "transparent",
                },
                "&:hover fieldset": {
                  borderColor: "#42A5F5",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "#42A5F5",
                  borderWidth: 2,
                },
                "&.Mui-error fieldset": {
                  borderColor: "#d32f2f",
                },
              },
            }}
          />
          {/* Password Requirements */}
          {values.password && (
            <Box sx={{ mt: 1.5 }}>
              <Typography
                variant="caption"
                sx={{
                  color: errors.password ? "#d32f2f" : "#666",
                  display: "block",
                  mb: 0.5,
                }}
              >
                Password requirements:
              </Typography>
              <Box component="ul" sx={{ pl: 2, m: 0 }}>
                <Typography
                  variant="caption"
                  component="li"
                  sx={{
                    color: values.password.length >= 6 ? "#4CAF50" : "#999",
                  }}
                >
                  Minimum 6 characters
                </Typography>
                <Typography
                  variant="caption"
                  component="li"
                  sx={{
                    color: /[A-Z]/.test(values.password) ? "#4CAF50" : "#999",
                  }}
                >
                  One uppercase character
                </Typography>
                <Typography
                  variant="caption"
                  component="li"
                  sx={{
                    color: /[a-z]/.test(values.password) ? "#4CAF50" : "#999",
                  }}
                >
                  One lowercase character
                </Typography>
                <Typography
                  variant="caption"
                  component="li"
                  sx={{
                    color: /[!@#$%^&*]/.test(values.password)
                      ? "#4CAF50"
                      : "#999",
                  }}
                >
                  One special character
                </Typography>
                <Typography
                  variant="caption"
                  component="li"
                  sx={{
                    color: /[0-9]/.test(values.password) ? "#4CAF50" : "#999",
                  }}
                >
                  One number
                </Typography>
              </Box>
            </Box>
          )}
        </Box>

        {/* Action Buttons */}
        <Box
          sx={{
            display: "flex",
            gap: 2,
            flexDirection: { xs: "column", sm: "row" },
          }}
        >
          <Button
            fullWidth
            variant="outlined"
            size="large"
            onClick={handleReset}
            disabled={isLoading}
            sx={{
              borderRadius: "30px",
              padding: "12px 24px",
              fontSize: "1rem",
              fontWeight: 600,
              textTransform: "none",
              borderColor: "#42A5F5",
              color: "#42A5F5",
              "&:hover": {
                borderColor: "#1565C0",
                backgroundColor: "rgba(66, 165, 245, 0.04)",
              },
            }}
          >
            Reset
          </Button>
          <Button
            fullWidth
            variant="contained"
            size="large"
            onClick={handleCreateUser}
            disabled={isLoading}
            sx={{
              borderRadius: "30px",
              padding: "12px 24px",
              fontSize: "1rem",
              fontWeight: 600,
              textTransform: "none",
              background: "linear-gradient(135deg, #42A5F5 0%, #1565C0 100%)",
              boxShadow: "0 4px 15px rgba(66, 165, 245, 0.4)",
              transition: "all 0.3s ease",
              "&:hover": {
                background: "linear-gradient(135deg, #1565C0 0%, #0D47A1 100%)",
                boxShadow: "0 6px 20px rgba(66, 165, 245, 0.6)",
                transform: "translateY(-2px)",
              },
              "&:active": {
                transform: "translateY(0)",
              },
              "&:disabled": {
                background: "#ccc",
                color: "#666",
              },
            }}
          >
            {isLoading ? (
              <CircularProgress size={24} sx={{ color: "#fff" }} />
            ) : (
              "Create User"
            )}
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

export default CreateUser;
